/**
 * PRCard.jsx
 *
 * Displays a compact PR badge and an expandable inline panel.
 * Matches the PR badge style from the DLC screenshots.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './PRCard.css';

const PR_STATES = {
  open: { label: 'Open', cls: 'pr-open' },
  merged: { label: 'Merged', cls: 'pr-merged' },
  changes: { label: 'Changes', cls: 'pr-changes' },
  draft: { label: 'Draft', cls: 'pr-draft' },
  closed: { label: 'Closed', cls: 'pr-closed' },
};

function getPRState(linkedPR) {
  if (!linkedPR) return null;
  const raw = (linkedPR.state || '').toLowerCase();
  return PR_STATES[raw] || PR_STATES.open;
}

export default function PRCard({ linkedPR, onMerge, onRequestChanges }) {
  const [expanded, setExpanded] = useState(false);

  if (!linkedPR) return null;

  const state = getPRState(linkedPR);

  return (
    <div className="pr-card">
      <button
        className={`pr-badge ${state.cls}`}
        onClick={() => setExpanded(e => !e)}
        title={`PR #${linkedPR.number} — ${state.label}`}
        type="button"
      >
        <span className="pr-icon">↕</span>
        <span className="pr-label">PR #{linkedPR.number}</span>
        <span className={`pr-state-pill ${state.cls}`}>{state.label}</span>
      </button>

      {expanded && (
        <div className="pr-panel">
          <div className="pr-panel-header">
            <span className="pr-panel-title">PR #{linkedPR.number}</span>
            {linkedPR.title && <span className="pr-panel-desc">{linkedPR.title}</span>}
            <button
              className="pr-panel-close"
              onClick={() => setExpanded(false)}
              type="button"
            >×</button>
          </div>

          {linkedPR.files && linkedPR.files.length > 0 && (
            <div className="pr-files">
              <div className="pr-files-heading">Files Changed</div>
              {linkedPR.files.map((f, i) => (
                <div key={i} className="pr-file-row">
                  <span className="pr-file-path">{f.path}</span>
                  {f.status && <span className={`pr-file-status pr-file-${f.status}`}>{f.status}</span>}
                  {(f.additions !== undefined || f.deletions !== undefined) && (
                    <span className="pr-file-diff">
                      {f.additions !== undefined && <span className="pr-add">+{f.additions}</span>}
                      {f.deletions !== undefined && <span className="pr-del">-{f.deletions}</span>}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {linkedPR.url && (
            <a
              className="pr-github-link"
              href={linkedPR.url}
              rel="noopener noreferrer"
              target="_blank"
            >Open full PR ↗</a>
          )}

          <div className="pr-panel-actions">
            {onRequestChanges && (
              <button
                className="pr-btn pr-btn-changes"
                onClick={() => { onRequestChanges(linkedPR); setExpanded(false); }}
                type="button"
              >Request Changes</button>
            )}
            {onMerge && state.cls === 'pr-open' && (
              <button
                className="pr-btn pr-btn-merge"
                onClick={() => { onMerge(linkedPR); setExpanded(false); }}
                type="button"
              >Merge PR</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

PRCard.propTypes = {
  linkedPR: PropTypes.shape({
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    state: PropTypes.string,
    url: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.shape({
      path: PropTypes.string,
      status: PropTypes.string,
      additions: PropTypes.number,
      deletions: PropTypes.number,
    })),
  }),
  onMerge: PropTypes.func,
  onRequestChanges: PropTypes.func,
};
